import type { ICustomImage } from "@filmweb/sanity-types";
import type IFilmnytt from "src/lib/types/Filmnytt";
import { DocType_Filmnytt } from "src/lib/types/Filmnytt";
import type { IFilmomtale } from "src/lib/types/Filmomtale";
import { DocType_Filmomtale } from "src/lib/types/Filmomtale";
import { DocType_Forsiden as DocType_Forsiden_v2, FRONTPAGE_PAGE_ID } from "src/lib/types/IForsidenV2";

import type { SanityDocument } from "sanity";
import { DocType_Biografi } from "src/lib/types/IBiografi";
import { DocType_PremiumLandingPage } from "src/lib/types/IPremiumLandingPage";

const BRAND = process.env.NEXT_PUBLIC_WEBSITE_BRAND!;

const brandUrlMap = {
	kinodk:
		{ "filmnytt": "nyheder" }
	,
	filmweb:
		{ "filmnytt": "filmnytt" }

}

/**
 * Creates an url for a given document.
 * NOTE: Only document with an actual route should be represented here!
 * @param docType The document type
 * @param slug the document's slug
 * @returns the url or null if the document type is not supported
 */
export function getUrlForDoctype(docType: string, slug: string | undefined | null, isStreamingContext: boolean = false, document?: SanityDocument): string | null {
	switch (docType) {
		case DocType_Filmnytt:
			if ((document?.extLink as string)?.trim()) {
				return (document?.extLink as string)?.trim();
			}
			return `/${isStreamingContext ? 'streamingguide/artikkel' : brandUrlMap[BRAND]["filmnytt"]}/${slug}`
		case DocType_Filmomtale:
			const d = document as IFilmomtale | undefined | null;
			if (BRAND === "filmweb") {
				return `/film/${slug ?? d?.mainVersionEDI}`;
			} else {
				return `/film/${slug ?? d?.fomId ?? d?.drupalSlug?.current}`;
			}
		case DocType_Forsiden_v2:
			if (slug!.endsWith(FRONTPAGE_PAGE_ID)) {
				return "/";
			} else if (slug!.endsWith("streamingguide_v2")) {
				return "/streamingguide";

			} else if (slug!.endsWith("filmnytt_v1")) {
				return "/" + brandUrlMap[BRAND]["filmnytt"];
			}
		case DocType_PremiumLandingPage:
			//const pl = document as IPremiumLandingPage | null | undefined;
			return `/program/${slug}`
		case DocType_Biografi:
			return `/${BRAND === "kinodk" ? "personer" : "navn"}/${slug}`;
		default:
		//console.error(`Unknown ${docType} / Slug: ${slug} / SG Context: ${isStreamingContext} `);
	}
	return null;
}

/**
 * URL to a person's page, from the person's name.
 * Spaces become underscores, which is what the person page and the revalidation of biografi expect.
 * @param personName the person's name, e.g. "Tom Rhys Harries"
 * @returns /navn/Tom_Rhys_Harries on filmweb, /personer/Tom_Rhys_Harries on kino.dk
 */
export function getPersonUrl(personName: string): string {
	return `/${BRAND === "kinodk" ? "personer" : "navn"}/${encodeURI(personName.replaceAll(" ", "_"))}`;
}

/**
 * Path for a person page's canonical link: the biografi's slug when there is a biografi, otherwise the URL built from the name.
 * @param biografiSlug the biografi's slug, if the person has one
 * @param personName the person's name, e.g. "Kristen Wiig"
 */
export function getPersonCanonicalPath(biografiSlug: string | undefined | null, personName: string): string {
	return biografiSlug ? getUrlForDoctype(DocType_Biografi, biografiSlug)! : getPersonUrl(personName);
}

/**
 * Helper function to get an article's Image
 * @param document the document
 * @returns the image or undefined if no image is available
 */
export function getImage(document: IFilmnytt | IFilmomtale): ICustomImage | undefined {
	switch (document._type) {
		case DocType_Filmnytt:
			if (Array.isArray((document as IFilmnytt).imageV2)) {
				return (document as IFilmnytt).imageV2?.[0] as ICustomImage | undefined;
			} else {
				return (document as IFilmnytt).imageV2 as ICustomImage | undefined;
			}

		case DocType_Filmomtale:
			const filmomtale = (document as IFilmomtale);
			// TODO: THis should maybe be fixed in the query
			return (filmomtale.mainImageV2 ?? filmomtale.imagesV2?.[0] ?? (filmomtale as IFilmomtale & { imageV2?: ICustomImage }).imageV2) as ICustomImage | undefined;

	}
}